import { Component, effect, model, signal } from '@angular/core';
import { SessionService } from '../../services/session-service';
import { CommonModule, DatePipe } from '@angular/common';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDialog } from '@angular/material/dialog';
import { CreateSessionModalComponent } from './create-session-modal-component/create-session-modal-component';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GetRowIdParams, GridApi, GridReadyEvent } from 'ag-grid-community';
import { EligibilitySession } from '../../models/eligibility-session';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { IconButtonCellRendererComponent } from '../util/icon-button-cell-renderer-component/icon-button-cell-renderer-component';
import { ConfirmationDialogComponent } from '../util/confirmation-dialog-component/confirmation-dialog-component';
import { LoadingOverlayComponent } from '../util/loading-overlay-component/loading-overlay-component';
import { PaginatedResponse } from '../../models/paginated-response';
import { MatTooltip } from "@angular/material/tooltip"; 
import { MatDividerModule } from '@angular/material/divider';
import { AG_GRID_LOCALE_FR } from '@ag-grid-community/locale';
import { EditSessionNameDialogComponent } from '../session-sites-component/edit-session-name-dialog-component/edit-session-name-dialog-component';
import { SnackBarService } from '../../services/snack-bar-service';
import { HeaderComponent } from "../header-component/header-component";
import { MatCheckboxModule } from '@angular/material/checkbox';

@Component({
  selector: 'app-all-sessions-component',
  imports: [
    CommonModule,
    MatDividerModule,
    MatButtonModule,
    MatDialogModule,
    MatInputModule,
    MatFormFieldModule,
    MatIconModule,
    FormsModule,
    AgGridAngular,
    MatTooltip,
    HeaderComponent,
    MatCheckboxModule
  ],
  providers: [DatePipe],
  templateUrl: './all-sessions-component.html',
  styleUrl: './all-sessions-component.css',
})
export class AllSessionsComponent {
  private gridApi!: GridApi<any>;
  
  public readonly gridLocale = AG_GRID_LOCALE_FR;

  public readonly sessions = signal<EligibilitySession[]>([]);
  public readonly sessionCode = model<string>('');
  public readonly displayedArchived = signal<boolean>(false);
  private sessionCodeDebounceTimer: any;
  public readonly sessionCodeExists = signal<boolean>(false);
  public readonly isSessionCodeLoading = signal<boolean>(false);

  public readonly loadingOverlayComponent: any = LoadingOverlayComponent;
  public readonly loadingOverlayComponentParams: any = { loadingMessage: 'Chargement des résultats...' };
  
  columns: ColDef[] = [
    { 
      headerName: "Code", 
      field: "code",
      maxWidth: 200
    },
    { 
      headerName: "Nom", 
      field: "name",
      filter: "agTextColumnFilter",
      floatingFilter: true,
    },
    {
      headerName: "Nb. sites",
      field: "siteCount",
      filter: false,
      maxWidth: 200
    },
    { headerName: "Date de création", 
      field: "dateCreated", 
      valueFormatter: params => this.datePipe.transform(params.value, 'short') ?? '',
      maxWidth: 250
    },
    /*{ headerName: "Dernière modification", 
      field: "dateUpdated", 
      valueFormatter: params => this.datePipe.transform(params.value, 'short') ?? '' 
    },*/
    { 
      headerName: "", 
      cellRenderer: IconButtonCellRendererComponent,
      cellRendererParams: {
        icon: 'open_in_new',
        tooltip: 'Ouvrir cette session',
        onClick: (data: any, index: number) => this.openSession(data.code),
      },
      filter: false,
      sortable: false,
      suppressHeaderMenuButton: true,
      flex: 1,
      maxWidth: 75
    },
    {
      cellRenderer: IconButtonCellRendererComponent,
      cellRendererParams: {
        icon: 'edit',
        tooltip: 'Modifier cette session',
        onClick: (data: any, index: number) => this.editSession(data),
      },
      filter: false,
      sortable: false,
      suppressHeaderMenuButton: true,
      flex: 1,
      maxWidth: 75
    },
    {
      cellRenderer: IconButtonCellRendererComponent,
      cellRendererParams: {
        icon: 'content_copy',
        tooltip: 'Dupliquer cette session',
        onClick: (data: any, index: number) => this.duplicateSession(data.code),
      },
      filter: false,
      sortable: false,
      suppressHeaderMenuButton: true,
      flex: 1,
      maxWidth: 75
    },
    {
      cellRenderer: IconButtonCellRendererComponent,
      cellRendererParams: {
        icon: (data: any) => data.archived ? 'unarchive' : 'archive',
        tooltip: (data: any) => data.archived ? 'Désarchiver cette session' : 'Archiver cette session',
        onClick: (data: any, index: number) => this.archiveSession(data.code, data.archived, index),
      },
      filter: false,
      sortable: false,
      suppressHeaderMenuButton: true,
      flex: 1,
      maxWidth: 75
    },
    {
      cellRenderer: IconButtonCellRendererComponent,
      cellRendererParams: {
        icon: 'delete',
        tooltip: 'Supprimer cette session',
        onClick: (data: any, index: number) => this.deleteSession(data, index),
      },
      filter: false,
      sortable: false,
      suppressHeaderMenuButton: true,
      flex: 1,
      maxWidth: 75
    }
  ];

  constructor(
    private readonly sessionService: SessionService,
    private readonly dialog: MatDialog,
    private readonly router: Router,
    private readonly datePipe: DatePipe,
    private readonly snackBarService: SnackBarService
  ) {
    // Check if input session code exists in database
    effect(() => {
      const sessionCode = this.sessionCode();

      this.isSessionCodeLoading.set(true);

      clearTimeout(this.sessionCodeDebounceTimer);

      if (sessionCode === '') {
        this.sessionCodeExists.set(false);
        this.isSessionCodeLoading.set(false);
        return;
      }

      const debounceTime = 500;

      this.sessionCodeDebounceTimer = setTimeout(() => {
        this.sessionService.getSession(sessionCode).subscribe({
          next: () =>  this.sessionCodeExists.set(true),
          error: () => this.sessionCodeExists.set(false)
        });
        this.isSessionCodeLoading.set(false);
      }, debounceTime);
    });
  }

  ngOnInit(): void {
    this.sessionService.resetCurrentSession();
  }
  
  getRowId(params: GetRowIdParams) {
    return params.data.code;
  }

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.getSessions(); // Done after gridApi is set otherwise loading grid option can't be set
  }

  getSessions(): void {
    this.gridApi.setGridOption('loading', true);
    this.sessionService.getAllSessions(this.displayedArchived()).subscribe({
      next: (response: PaginatedResponse<EligibilitySession>) => {
        this.sessions.set(response.items);
        this.gridApi.setGridOption('loading', false);
      },
      error: () => {
        this.snackBarService.error("Erreur lors de la récupération des sessions");
        this.gridApi.setGridOption('loading', false);
      }
    });
  }

  checkSessionCodeThenOpenSession(): void {
    this.sessionService.getSession(this.sessionCode()).subscribe({
      next: () =>  this.openSession(this.sessionCode()),
      error: () => this.sessionCodeExists.set(false)
    });
  }

  openSession(sessionCode: string) {
    this.router.navigate(['sessions/', sessionCode]);
  }

  editSession(session: any) {
    const dialogRef = this.dialog.open(EditSessionNameDialogComponent, {
      data: {
        session: session
      }
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (!result) {
        return;
      }

      this.sessions.update(sessions => sessions.map(currentSession => currentSession.code === result.code ? result : currentSession));
    });
  }

  duplicateSession(sessionCode: string) {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {content: "Êtes-vous sûr de vouloir dupliquer cette session ?"},
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) {
        return;
      }

      this.sessionService.duplicateSession(sessionCode).subscribe({
        next: (session) => {
          this.router.navigate(['sessions/', session.code]);
          this.snackBarService.success("Session dupliquée.");
        },
        error: () => {
          this.snackBarService.error("Erreur lors de la duplication de session");
        }
      });
    });
  }

  createSession() {
    const dialogRef = this.dialog.open(CreateSessionModalComponent, {
      width: '400px'
    });

    dialogRef.afterClosed().subscribe((closeValue: boolean) => {
      if (closeValue) {
        this.getSessions();
      }
    });
  }

  archiveSession(sessionCode: string, archived: boolean, index: number): void {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {content: archived ? "Êtes-vous sûr de vouloir désarchiver cette session ?" : "Êtes-vous sûr de vouloir archiver cette session ?"},
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) {
        return;
      }

      this.sessionService.archiveSession(sessionCode, !archived).subscribe({
        next: () => {
          if (this.displayedArchived()) {
            this.sessions.update(sessions => sessions.map(session => 
              {
                if (session.code === sessionCode) {
                  session.archived = !archived;
                  return session;
                } else {
                  return session;
                }
              }
            ));
          } else {
            this.sessions.update(sessions => sessions.toSpliced(index, 1));
          }
          this.gridApi.redrawRows();
        },
        error: () => {
          this.snackBarService.error("Erreur rencontrée lors de la suppression de la session.");
        }
      });
    });
  }

  deleteSession(session: any, index: number): void {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {content: "Êtes-vous sûr de vouloir supprimer cette session ?"},
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) {
        return;
      }

      this.sessionService.deleteSession(session.code).subscribe({
        next: () => {
          this.sessions.update(sessions => sessions.toSpliced(index, 1));
        },
        error: () => {
          this.snackBarService.error("Erreur rencontrée lors de la suppression de la session.");
        }
      });
    });
  }

  clearSessionCode(): void {
    this.sessionCode.set('');
  }
}
